import { useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useUser } from '@/queries/useUser';
import {
    changeTier,
    estimateSubscriptionChange,
    type PaidSubscriptionTier,
    renewSubscription,
    SUBSCRIPTION_INTERVALS,
    SUBSCRIPTION_TIERS,
    type SubscriptionInterval,
} from '@/lib/api/subscriptions';
import { stanNotify } from '@/lib';
import { formatCurrency } from '@/lib/currency';
import { format } from 'date-fns';
import { SUBSCRIPTION_STATUS } from '../constants';
import { useSubscriptionPrices } from '@/hooks/useSubscriptionPrices';

export interface EstimateData {
    cost: string;
    date: string;
    status: string;
}

interface UseSubscriptionChangeOptions {
    onSuccess?: () => void;
}

/**
 * Core hook for managing subscription changes
 * Handles tier/interval selection, estimation, and execution of subscription changes
 */
export const useSubscriptionChange = (options?: UseSubscriptionChangeOptions) => {
    const { user, refetchUser } = useUser();
    const { currency, getPriceId } = useSubscriptionPrices();

    // Get current subscription details
    const platformSubscription = user?.subscriptions?.platform;
    const currentTier = (platformSubscription?.data?.subscription_tier ||
        SUBSCRIPTION_TIERS.CREATOR) as PaidSubscriptionTier;
    const currentInterval = (platformSubscription?.data?.price?.interval ||
        SUBSCRIPTION_INTERVALS.MONTH) as SubscriptionInterval;
    const platformSubscriptionId = platformSubscription?.stripe_id || '';
    const subscriptionIsCancelled = platformSubscription?.status === SUBSCRIPTION_STATUS.CANCELLED;

    // State for new subscription selection
    const [newTier, setNewTier] = useState<PaidSubscriptionTier>(currentTier);
    const [newInterval, setNewInterval] = useState<SubscriptionInterval>(currentInterval);
    const [estimate, setEstimate] = useState<EstimateData | null>(null);

    // Estimate mutation
    const estimateMutation = useMutation({
        mutationFn: async () => {
            const priceId = getPriceId(newTier, newInterval);
            return estimateSubscriptionChange(platformSubscriptionId, priceId);
        },
        onSuccess: data => {
            setEstimate({
                cost: formatCurrency(Math.abs(data.estimated_cost) / 100, currency),
                date: format(new Date(data.start_date * 1000), 'MMM d, yyyy'),
                status: data.status,
            });
        },
        onError: error => {
            console.error('Failed to estimate subscription change:', error);
        },
    });

    // Change tier mutation
    const changeTierMutation = useMutation({
        mutationFn: async () => {
            const priceId = getPriceId(newTier, newInterval);
            await changeTier({ stripe_id: platformSubscriptionId, price_id: priceId });
        },
        onSuccess: () => {
            stanNotify({ type: 'success', title: 'Success!', text: 'We have updated your subscription!' });
            refetchUser();
            options?.onSuccess?.();
        },
        onError: (error: any) => {
            stanNotify({
                type: 'error',
                title: 'Something went wrong',
                text: error?.response?.data?.message ?? 'Please try again later.',
            });
        },
    });

    // Renew subscription mutation
    const renewMutation = useMutation({
        mutationFn: async () => {
            await renewSubscription({ stripe_id: platformSubscriptionId });
        },
        onSuccess: () => {
            stanNotify({ type: 'success', title: 'Success!', text: 'Your subscription has been renewed.' });
            refetchUser();
            options?.onSuccess?.();
        },
        onError: (error: any) => {
            stanNotify({
                type: 'error',
                title: 'Something went wrong',
                text: error?.response?.data?.message ?? 'Please try again later.',
            });
        },
    });

    const hasPlanBeenEdited = newTier !== currentTier || newInterval !== currentInterval;

    const isCurrentPlan = (tier: PaidSubscriptionTier) => {
        return tier === currentTier && currentInterval === newInterval;
    };

    // Execute the subscription change (either change tier or renew)
    const executeChange = () => {
        if (subscriptionIsCancelled) {
            renewMutation.mutate();
        } else {
            changeTierMutation.mutate();
        }
    };

    // Reset selections to current subscription
    const reset = useCallback(() => {
        setNewTier(currentTier);
        setNewInterval(currentInterval);
        setEstimate(null);
    }, [currentTier, currentInterval]);

    // Request an estimate for the current selection
    const requestEstimate = () => {
        if (platformSubscriptionId && (hasPlanBeenEdited || subscriptionIsCancelled)) {
            estimateMutation.mutate();
        }
    };

    const isLoading = changeTierMutation.isPending || renewMutation.isPending || estimateMutation.isPending;

    return {
        // Current state
        currentTier,
        currentInterval,
        subscriptionIsCancelled,
        platformSubscriptionId,

        // New selection
        newTier,
        newInterval,
        setNewTier,
        setNewInterval,

        // Computed values
        hasPlanBeenEdited,
        isCurrentPlan,
        estimate,
        isLoading,

        // Actions
        executeChange,
        reset,
        requestEstimate,
    };
};
